/**
 * useDiscardGuard — shared close/discard orchestration for the ConnectionList
 * edit dialogs.
 *
 * Every first-class edit dialog (managed/ARM, service-provider, agent) routes
 * all of its close affordances (Esc / backdrop / Cancel / Close-X) through the
 * same guard so unsaved edits can't be lost by a path that skips the check:
 *
 *   1. While a save/mutation is in flight (`isBusy`), close is a no-op.
 *   2. If the form has unsaved edits (`isDirty`), show the confirmation dialog
 *      instead of closing.
 *   3. Otherwise close immediately.
 *
 * Discarding is always an explicit button click — `confirmDiscard` runs an
 * optional cleanup (e.g. `form.resetToOriginal()`, so a re-open doesn't show
 * stale edits) and then closes. This hook owns only the confirmation-visibility
 * state; the caller renders `<DiscardChangesDialog>` with the returned props.
 */

import { useCallback, useState } from 'react';

export interface UseDiscardGuardOptions {
  /** Whether the form currently has unsaved edits. */
  isDirty: boolean;
  /**
   * Whether a save/mutation is in flight. While busy, every close affordance
   * is a no-op so the user can't dismiss the dialog mid-write.
   */
  isBusy: boolean;
  /** Close the parent dialog (the caller's `onClose`). */
  onClose: () => void;
  /**
   * Optional cleanup run immediately before closing on an explicit discard —
   * e.g. `form.resetToOriginal()` so re-opening doesn't surface stale edits.
   * Dialogs without a form snapshot (e.g. the agent dialog) omit this.
   */
  onDiscard?: () => void;
}

export interface DiscardGuard {
  /** True while the discard-confirmation dialog should be shown. */
  showDiscardConfirm: boolean;
  /**
   * Route every close affordance through here: no-op while busy, prompt to
   * confirm when dirty, otherwise close.
   */
  requestClose: () => void;
  /** Dismiss the confirmation and stay on the edit dialog. */
  keepEditing: () => void;
  /** Confirm discard: run the optional cleanup, hide the prompt, then close. */
  confirmDiscard: () => void;
  /**
   * Adapter for Fluent's `Dialog.onOpenChange` that funnels close intents
   * (open → false) through `requestClose`.
   */
  handleOpenChange: (event: unknown, data: { open: boolean }) => void;
}

export function useDiscardGuard({
  isDirty,
  isBusy,
  onClose,
  onDiscard,
}: UseDiscardGuardOptions): DiscardGuard {
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const requestClose = useCallback(() => {
    if (isBusy) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  }, [isBusy, isDirty, onClose]);

  const keepEditing = useCallback(() => {
    setShowDiscardConfirm(false);
  }, []);

  const confirmDiscard = useCallback(() => {
    onDiscard?.();
    setShowDiscardConfirm(false);
    onClose();
  }, [onDiscard, onClose]);

  const handleOpenChange = useCallback(
    (_event: unknown, data: { open: boolean }) => {
      if (!data.open) requestClose();
    },
    [requestClose],
  );

  return {
    showDiscardConfirm,
    requestClose,
    keepEditing,
    confirmDiscard,
    handleOpenChange,
  };
}
